// Chill guitar lofi (docs/AUDIO_DESIGN.md §12): a clean electric guitar on the neck pickup (warm amp, chorus, a touch of
// spring) fingerpicks open, voice-led guitar shapes and strums softly on some bars, over a lazy swung boom-bap kit and a
// fingered electric bass on the root and fifth. Short pentatonic licks with slides and hammer-ons on most tracks.
import { rng } from '../../engine/core.js';
import { QUALITIES, bassNote, scaleNotes, playedChords } from '../harmony.js';
import { createElectric, createElectricLead } from './guitar-voice.js';
import { createFingerBass, createDustKit } from './guitar-band.js';

const mod12 = n => ((n % 12) + 12) % 12;
const E_IX = { chill: 0, balanced: 1, upbeat: 2 };

/* ---------------- guitar voicings: real shapes on six strings (E2 A2 D3 G3 B3 E4) ---------------- */
// The root in the bass (MIDI 40–52) + 3 or 4 upper tones: every order of the chord's tones without the root, stacked
// upward from a 4th above the bass (a minor 3rd from MIDI 45), each the nearest above the last (close, drop-2, drop-3 and
// spread shapes). The 3rd (or the sus 4th) and the 7th (or 6th) are always there; 5-tone chords may drop the 5th or the
// 9th; 4-tone ones may double the root, 5th or 3rd on top (the open-chord sound). Kept: top 57–76, upper span ≤ 17, no
// minor 2nd between neighbours, and a fingering: one note per string bottom to top, frets 0–12, the fretted notes within
// 4 frets (open strings are free).
const OPEN = [40, 45, 50, 55, 59, 64];
/** the easiest fingering of an ascending voicing → { open, pos (lowest fret), span } | null */
function fingering(v) {
  let best = null;
  const walk = (i, s0, lo, hi, open) => {
    if (i === v.length) {
      const pos = hi < 0 ? 0 : lo, span = hi < 0 ? 0 : hi - lo;
      if (!best || open > best.open || (open === best.open && span + pos * 0.2 < best.span + best.pos * 0.2)) best = { open, pos, span };
      return;
    }
    for (let s = s0; s <= 6 - (v.length - i); s++) {
      const f = v[i] - OPEN[s];
      if (f < 0 || f > 12) continue;
      if (f === 0) walk(i + 1, s + 1, lo, hi, open + 1);
      else { const l = Math.min(lo, f), h = Math.max(hi, f); if (h - l <= 4) walk(i + 1, s + 1, l, h, open); }
    }
  };
  walk(0, 0, 99, -1, 0);
  return best;
}
const combos = (a, k) => (k === 0 ? [[]] : a.flatMap((x, i) => combos(a.slice(i + 1), k - 1).map(c => [x, ...c])));
const perms = a => (a.length <= 1 ? [a] : a.flatMap((x, i) => perms([...a.slice(0, i), ...a.slice(i + 1)]).map(p => [x, ...p])));
const SHAPES = new Map();                                                   // `${root}:${q}` → [{ v, u, b, f, mean }]
function shapes(root, q) {
  const key = `${root}:${q}`;
  if (SHAPES.has(key)) return SHAPES.get(key);
  const up = [...new Set(QUALITIES[q].map(i => i % 12).filter(i => i))];
  const third = up.find(i => i === 3 || i === 4) ?? up.find(i => i === 5 || i === 2), sev = up.find(i => i === 10 || i === 11) ?? up.find(i => i === 9);
  const guides = [third, sev].filter(x => x != null), tens = QUALITIES[q].filter(i => i > 12).map(i => i % 12), sets = [];
  for (const k of [3, 4]) for (const s of combos(up, k)) if (guides.every(g => s.includes(g))) sets.push({ s, top: null });
  if (up.length === 3) for (const d of [0, 7, third]) if (d != null && (d === 0 || up.includes(d))) sets.push({ s: up, top: d });
  const out = [], seen = new Set(), basses = [];
  for (let m = 40 + mod12(root - 40); m <= 52; m += 12) basses.push(m);
  const above = (m, iv) => m + 1 + mod12(root + iv - m - 1);                 // the nearest note above m with that interval
  for (const { s, top } of sets) for (const pm of perms(s)) for (const b of basses) {
    for (let u0 = above(b + (b < 45 ? 5 : 3) - 1, pm[0]); u0 <= 64; u0 += 12) {
      const u = [u0];
      for (let i = 1; i < pm.length; i++) u.push(above(u[i - 1], pm[i]));
      if (top != null) u.push(above(u[u.length - 1], top));
      const t = u[u.length - 1], v = [b, ...u], id = v.join();
      if (t < 57 || t > 76 || t - u[0] > 17 || seen.has(id) || u.some((x, i) => i && x - u[i - 1] < 2)) continue;
      seen.add(id);
      const f = fingering(v), colour = u.some(m => tens.includes(mod12(m - root)));
      if (f) out.push({ v, u, b, f, colour, mean: u.reduce((x, y) => x + y, 0) / u.length });
    }
  }
  SHAPES.set(key, out);
  return out;
}
/** movement between the upper voices (different sizes: each note to the nearest of the other chord, +1) */
function move(u, pu) {
  if (u.length === pu.length) return u.reduce((s, x, i) => s + Math.abs(x - pu[i]), 0);
  const near = (x, a) => Math.min(...a.map(y => Math.abs(x - y)));
  return (u.reduce((s, x) => s + near(x, pu), 0) + pu.reduce((s, y) => s + near(y, u), 0)) / 2 + 1;
}
/** Cost: .4·|mean(upper) − home| + .6·max(0, top − 72) − .7 per open string + .3·max(0, position − 7) + 1.2 for a 4-fret
 *  stretch − .8 with the chord's tension (its 9th, 11th or 13th); after a chord + Σ|Δupper| + .45·|Δbass| +
 *  2·max(0, |Δtop| − 3). home = 61, moved by plan.register (−5..+5) for the track's first chord only, which also pays
 *  .5·|top − (67 + register)| (else the open high E wins the top voice on one track in five), so tracks don't all open on
 *  the same notes. The same chord again keeps its shape. */
export function guitarVoicing(sp, prev, c, plan) {
  const reg = plan.register || 0, cands = shapes(mod12(plan.key.tonic + c.root), c.q), home = 61 + (prev ? 0 : reg);
  if (!cands.length) return [40 + mod12(plan.key.tonic + c.root - 40), ...sp.iv.map(i => 52 + mod12(sp.root + i - 52)).sort((a, b) => a - b)];
  if (prev && cands.some(x => x.v === prev)) return prev;
  let best = cands[0], bc = Infinity;
  for (const x of cands) {
    const top = x.u[x.u.length - 1];
    let k = 0.4 * Math.abs(x.mean - home) + 0.6 * Math.max(0, top - 72) - 0.7 * x.f.open + 0.3 * Math.max(0, x.f.pos - 7)
      + (x.f.span >= 4 ? 1.2 : 0) - (x.colour ? 0.8 : 0);
    if (prev) { const pu = prev.slice(1); k += move(x.u, pu) + 0.45 * Math.abs(x.b - prev[0]) + 2 * Math.max(0, Math.abs(top - pu[pu.length - 1]) - 3); }
    else k += 0.5 * Math.abs(top - 67 - reg);
    if (k < bc) { bc = k; best = x; }
  }
  return best.v;
}

/* ---------------- bass: root on the kicks, a fifth on some off-beat kicks, an approach into a new chord ---------------- */
function bassLine({ plan, st, mode, chords, nextChords, chordAt, kicks, stop }) {
  if (mode !== 'kick') return null;                                        // intro, break, outro: the default roots
  const E = E_IX[plan.vibe.energy] ?? 1, r = st.rS, pc = c => mod12(plan.key.tonic + c.root);
  const changes = chords.map(c => c.start * 4), last = chords[chords.length - 1], nx = nextChords && nextChords[0];
  let steps = [...new Set([...kicks, ...changes])].sort((a, b) => a - b), approach = false;
  if (nx && pc(nx) !== pc(last) && r() < [0.25, 0.35, 0.45][E]) { steps = steps.filter(s => s < 14); steps.push(14); approach = true; }
  if (stop != null) steps = steps.filter(s => s < stop);
  const end = stop ?? 16;
  return steps.map((s, k) => {
    const c = chordAt(s), root = bassNote(pc(c), st.prevBass), len = (k + 1 < steps.length ? steps[k + 1] : end) - s;
    let midi = root, vel = s === 0 ? 0.92 : s % 4 ? 0.74 : 0.84, slide = false, dur = len * plan.bass.factor;
    if (approach && s === 14) {
      const tg = bassNote(pc(nx), root), u = r();
      midi = u < 0.4 ? tg - 1 : u < 0.6 ? tg + 1 : tg - 5 >= 33 ? tg - 5 : tg + 7; vel = 0.72; slide = r() < 0.3; dur = Math.min(len, 1.6);
    } else if (!changes.includes(s) && s % 8 && r() < [0.3, 0.4, 0.45][E]) {
      const iv = QUALITIES[c.q], f = iv.includes(7) ? 7 : iv.includes(6) ? 6 : 7;
      midi = r() < 0.85 ? (root + f <= 50 ? root + f : root + f - 12) : root + 12 <= 52 ? root + 12 : root;
    } else st.prevBass = root;
    return { s, midi, vel, dur: Math.max(0.5, dur), slide };
  });
}

/* ---------------- lead licks: one per track, 2 bars, short and lyrical (the engine's phrase() restates it) ---------------- */
// [steps, gaps]: 'l' = legato into the next note (the engine glides: a hammer-on, pull-off or slide), '.' = picked
const LICKS = [
  [[0, 2, 3, 6], '.ll'], [[2, 4, 6, 7, 10], '..ll'], [[0, 3, 4, 8, 12], '.l.l'], [[4, 6, 7, 10, 16], 'll..'],
  [[0, 1, 4, 6, 14], 'l.l.'], [[6, 8, 9, 12, 18], '.ll.'], [[0, 4, 6, 8, 11, 12], '.l..l'], [[3, 4, 6, 10], 'll.'],
];
/** a motif for melody.js phrase(): legato gaps move 1 scale step (hammer-on / pull-off) or 2 (a slide), picked gaps
 *  ±1–2 steps (leaning down) or a leap of 3; the last note rings 5–9 steps */
function lickMotif(r, key) {
  const [steps, gaps] = LICKS[Math.floor(r() * LICKS.length)], n = steps.length, last = 5 + Math.floor(r() * 5);
  const durs = steps.map((s, i) => (i === n - 1 ? last : gaps[i] === 'l' ? Math.min(6, steps[i + 1] - s) : Math.max(1, Math.min(6, steps[i + 1] - s) - 2)));
  const contour = [0];
  let dir = r() < 0.6 ? -1 : 1;
  for (let i = 1; i < n; i++) {
    const mv = gaps[i - 1] === 'l' ? (r() < 0.7 ? 1 : 2) * (r() < 0.5 ? 1 : -1) : r() < 0.8 ? (1 + Math.floor(r() * 2)) * (r() < 0.65 ? dir : -dir) : 3 * dir;
    let v = contour[i - 1] + mv;
    if (Math.abs(v) > 4) { v = contour[i - 1] - mv; dir = -dir; }
    contour.push(v);
  }
  return { steps, durs, contour, vel: steps.map((s, i) => (i === 0 ? 0.8 : i === n - 1 ? 0.74 : +(0.64 + 0.1 * r()).toFixed(3))), mode: key.mode };
}

/* ---------------- the style ---------------- */
const T = [0], UP = [-1, -2, -3], CHK = [-3, -2, -1];   // picks: the thumb (bass note), an up-strum (top 3, top first), a muted strum
export default {
  id: 'guitar', name: 'Guitar', desc: 'Clean electric guitar, fingerpicked chords and soft licks over a lazy beat',
  vary: 'jazz',
  energy: {
    //            chill                                    balanced                                  upbeat
    bpm:        [[66, 74],                                 [72, 82],                                 [80, 90]],
    swing:      [[57, 61],                                 [55, 60],                                 [54, 58]],
    snareLag:   [[0.012, 0.022],                           [0.008, 0.017],                           [0.005, 0.012]],
    groove:     [[{ half: 1.3, rim: 1.4, dusty: 0.8, nod: 0.4 }, { rim: 1.2, dusty: 1, half: 0.8, nod: 0.6 }],
                 [{ dusty: 1.2, rim: 1, nod: 0.8, half: 0.6 }, { dusty: 1.2, nod: 1.1, rim: 0.7, skip: 0.6 }],
                 [{ dusty: 1, nod: 1.2, skip: 1, rim: 0.5 }, { dusty: 1, nod: 1.2, skip: 1.1, rim: 0.4 }]],
    stretch:    [0.5,                                      0.35,                                     0.2],
    drumLevel:  [0.8,                                      0.9,                                      0.95],
    kitLp:      [[4200, 6000],                             [5000, 7200],                             [5800, 8200]],
    epLp:       [[2400, 3000],                             [2700, 3400],                             [3000, 3800]],     // the tone LP
    strum:      [[0.024, 0.04],                            [0.018, 0.032],                           [0.015, 0.026]],
    epVel:      [[0.52, 0.6],                              [0.54, 0.63],                             [0.56, 0.66]],
    comp:       [{ ballad: [0.9, 1.4], ring: [0.5, 1], pinch: [0.5, 0.9], arp: [0.3, 0.7], travis: [0, 0.2], mix: [0.05, 0.25], strum: [0, 0.1] },
                 { ballad: [0.3, 0.6], ring: [0.1, 0.3], pinch: [0.4, 0.8], arp: [0.6, 1.1], travis: [0.3, 0.7], mix: [0.3, 0.7], strum: [0.2, 0.5] },
                 { ballad: [0, 0.2], ring: [0, 0.1], pinch: [0.2, 0.5], arp: [0.4, 0.8], travis: [0.6, 1], mix: [0.5, 0.9], strum: [0.5, 0.9] }],
    push:       [0.1,                                      0.2,                                      0.3],
    legato:     [0.8,                                      0.65,                                     0.5],
    lead:       [0.45,                                     0.75,                                     0.85],
    softLead:   [0.75,                                     0.6,                                      0.5],              // the neck tone, else a brighter one
    tone:       [[4200, 6400],                             [5200, 8000],                             [6200, 9000]],
    wowCents:   [[6, 11],                                  [5, 9],                                   [4, 8]],
    dust:       [[0.9, 1.5],                               [0.7, 1.3],                               [0.6, 1.1]],
  },
  progressions: {
    major: {
      G1: [[5, 'maj7', 4], [4, 'm7', 4], [9, 'm7', 4], [7, '9', 4]],                                   // IVmaj7 · iii7 · vi7 · V9
      G2: [[2, 'm9', 4], [7, '13', 4], [0, 'maj9', 4], [0, 'maj9', 4]],                                // ii9 · V13 · Imaj9
      G3: [[0, 'maj7', 4], [9, 'm9', 4], [2, 'm9', 4], [7, '7sus4', 4]],                               // Imaj7 · vi9 · ii9 · V7sus4
      G4: [[0, 'add9', 4], [4, 'm7', 4], [5, 'maj7', 4], [5, 'm6', 4]],                                // Iadd9 · iii7 · IVmaj7 · iv6
      G5: [[5, 'maj9', 4], [4, 'm7', 4], [2, 'm9', 2], [1, 'maj7', 2], [0, '69', 4]],                  // IV · iii · ii9|♭IImaj7 · I6/9
      G6: [[5, 'maj7', 4], [4, 'm7', 4], [3, 'm7', 4], [2, 'm9', 2], [7, '7sus4', 2]],                 // IV · iii7 · ♭iii7 · ii|V7sus4
    },
    minor: {
      g1: [[0, 'm9', 4], [5, 'm7', 4], [0, 'm9', 4], [5, '9', 4]],                                     // im9 · ivm7 · im9 · IV9
      g2: [[0, 'm9', 4], [8, 'maj7', 4], [10, '69', 4], [0, 'm7', 4]],                                 // im9 · ♭VImaj7 · ♭VII6/9 · im7
      g3: [[5, 'm9', 4], [10, '13', 4], [3, 'maj7', 4], [8, 'maj7', 4]],                               // ivm9 · ♭VII13 · ♭IIImaj7 · ♭VImaj7
      g4: [[0, 'm9', 4], [5, 'm7', 4], [7, 'm7', 4], [8, 'maj7', 4]],                                  // im9 · ivm7 · vm7 · ♭VImaj7
      g5: [[0, 'm9', 4], [8, 'maj7', 4], [5, 'm9', 4], [1, 'maj7', 4]],                                // im9 · ♭VImaj7 · ivm9 · ♭IImaj7
      g6: [[0, 'madd9', 4], [10, '69', 4], [8, 'maj7', 4], [7, '7sus4', 4]],                           // im(add9) · ♭VII6/9 · ♭VImaj7 · V7sus4
    },
  },
  turnarounds: { major: [[2, 'm9', 2], [7, '7sus4', 2]], minor: [[8, 'maj7', 2], [7, '7sus4', 2]] },   // ii9|V7sus4 · ♭VImaj7|V7sus4
  grids: {
    //         0123456789abcdef
    dusty: { K: 'x.........x.....', S: '....x.......x...', H: 'x.x.x.x.x.x.x.x.' },                   // boom-bap
    nod:   { K: 'x......x..x.....', S: '....x.......x..g', H: 'x.x.x.x.x.x.x.xs' },                   // a kick on the 2-and
    rim:   { K: 'x.....x...x.....', R: '....x.......x...', H: 'x.x.x.x.x.x.x.x.' },                   // cross-stick backbeat
    half:  { K: 'x.....x.........', S: '........x.......', H: 'x.x.x.x.x.x.x.x.', R: '............x...' }, // halftime
    skip:  { K: 'x......x.x......', S: '....x.......x...', H: 'x.xsx.xsx.xsx.xs' },                   // skippy 16th hats
    break: { K: 'x.........x.....', R: '....x.......x...', H: 'x...x...x...x...' },
  },
  fills: {
    roll: { roll: [[12, 0.7], [14, 0.42], [15, 0.58]] },                                               // a soft snare pickup
    rim: { roll: [[13, 0.5], [14, 0.6], [15, 0.72]], voice: 'rim' },
    kicks: { kicks: [13, 15] },
    open: { open: 14 },
    stop: { stop: 12 },
  },
  // [step, length, vel, pick]: fingerpicking (one note at a time, let ring: the thumb on the bass note, fingers on the top
  // three), soft down/up strums (pick null = down, UP = the top three strings top first) and a muted ghost strum (CHK)
  comps: {
    ballad: [[0, 16, 0.95, [0, -1]], [4, 12, 0.7, [-2]], [8, 8, 0.85, T], [10, 6, 0.72, [-1]], [12, 4, 0.68, [-3]]],
    ring:   [[0, 16, 0.85], [8, 8, 0.7, [-2, -1]]],
    pinch:  [[0, 8, 0.95, [0, -1]], [3, 5, 0.66, [-2]], [6, 10, 0.74, [-3]], [8, 8, 0.88, [0, -2]], [11, 5, 0.68, [-1]]],
    arp:    [[0, 16, 0.95, T], [2, 14, 0.7, [-3]], [4, 12, 0.76, [-2]], [6, 10, 0.82, [-1]],
             [8, 8, 0.85, T], [10, 6, 0.72, [-2]], [12, 4, 0.78, [-1]], [14, 2, 0.68, [-3]]],
    travis: [[0, 8, 0.95, [0]], [2, 6, 0.72, [-1]], [4, 8, 0.84, [1]], [6, 6, 0.74, [-2]],           // the thumb alternates
             [8, 8, 0.9, [0]], [10, 6, 0.72, [-1]], [12, 4, 0.84, [1]], [14, 2, 0.7, [-2]]],
    strum:  [[0, 6, 0.88], [6, 2, 0.68, UP], [8, 1, 0.36, CHK], [10, 4, 0.8], [12, 2, 0.66, UP]],
    mix:    [[0, 8, 0.95, T], [2, 6, 0.72, [-2]], [4, 4, 0.78, [-1]], [6, 2, 0.7, [-3]], [8, 6, 0.82], [11, 3, 0.66, UP]],
  },
  keysLevel: 1.62,
  plan(plan, { r }) {
    const rn = (a, b, d = 3) => +(a + r() * (b - a)).toFixed(d), k = plan.drums.kit;
    plan.gtr = { drive: rn(1.4, 2.2, 2), chorus: { rate: rn(0.45, 0.85, 2), cents: rn(5, 9, 1) }, spring: rn(0.1, 0.18), pan: rn(-0.12, 0.04, 2) };
    plan.drums.kit = {
      kickF0: rn(112, 132, 1), kickF1: rn(44, 51, 1), kickDecay: rn(0.15, 0.2), snareF: rn(1500, 2000, 0), snareDecay: rn(0.09, 0.13),
      hatHP: rn(5200, 6800, 0), hatClosed: rn(0.014, 0.022), rimF: rn(470, 560, 0), lp: k.lp,
    };
    plan.bass.lp = rn(380, 480, 0);
    if (plan.lead) {
      plan.lead.timbre = plan.lead.timbre === 'soft' ? 'neck' : 'bright';
      plan.lead.pan = +((0.14 + r() * 0.14) * (plan.gtr.pan > -0.04 ? -1 : 1)).toFixed(2);          // across from the chords
      plan.lead.motif = lickMotif(r, plan.key);
    }
  },
  voicing: guitarVoicing,
  bass: bassLine,
  voices(ac, dry, wet, plan, ctx) {
    const o = { registry: ctx.registry, tag: ctx.tag, t0: ctx.t0 }, sub = () => rng(Math.floor(ctx.r() * 4294967296));
    const ep = createElectric(ac, dry, wet, { lp: plan.ep.lp, ...plan.gtr }, { ...o, cap: ctx.cap, r: sub(), log: ctx.log, stepDur: ctx.stepDur });
    ep.warm([...trackChords(plan).flat(), ...(plan.lead ? scaleNotes(plan.key, 67, 84) : [])], 3);
    const lead = plan.lead
      ? createElectricLead(ac, ep, wet, plan.lead, { ...o, r: sub(), stepDur: ctx.stepDur })
      : null;
    return { ep, bass: createFingerBass(ac, dry, plan.bass, o), kit: createDustKit(ac, dry, wet, plan.drums.kit, o), lead };
  },
};

/** the track's voicings, first bars first (progA, progB, the final tonic as in engine.js barChords), to render their
 *  plucks ahead of time */
function trackChords(plan) {
  const out = [], fin = { root: 0, q: plan.key.mode === 'minor' ? 'm9' : plan.seed % 2 ? 'maj9' : '69' };
  let prev = null;
  for (const [c, P] of playedChords(plan, fin)) {
    prev = guitarVoicing({ root: mod12(P.key.tonic + c.root), iv: [] }, prev, c, P);
    out.push(prev);
  }
  return out;
}
